import { svgPathBbox } from 'svg-path-bbox';
import type { LineMetric } from '@meitu/x-design-text';
import { unionBounds } from '@/utils/bounds-kit';
import type { Bounds } from '@/version-types';

/**
 * 读取一条 SVG path 的原始轴对齐边界。
 *
 * 本函数保持无状态，不建立全局缓存；需要重复消费同一轮廓的调用方应按自身生命周期缓存结果。
 * Glyph Pipeline 使用 render-scoped Map，使 geometry 与节点创建共享一次解析，同时避免跨 render 失效问题。
 *
 * @param path - 非空 SVG path 数据。
 * @returns path 在自身坐标系中的四向边界。
 */
export const getPathBounds = (path: string): Bounds => {
    const [left, top, right, bottom] = svgPathBbox(path);
    return { left, top, right, bottom };
};

/**
 * 将边界的四个角映射到目标坐标系并重新计算轴对齐边界。
 *
 * @param bounds - 变换前的四向边界。
 * @param transform - 将一个角点转换到目标坐标系的函数。
 * @returns 覆盖四个变换后角点的轴对齐边界。
 */
export const transformBounds = (
    bounds: Bounds,
    transform: (x: number, y: number) => { x: number; y: number },
): Bounds => {
    const points = [
        transform(bounds.left, bounds.top),
        transform(bounds.left, bounds.bottom),
        transform(bounds.right, bounds.top),
        transform(bounds.right, bounds.bottom),
    ];
    return {
        left: Math.min(...points.map((point) => point.x)),
        top: Math.min(...points.map((point) => point.y)),
        right: Math.max(...points.map((point) => point.x)),
        bottom: Math.max(...points.map((point) => point.y)),
    };
};

/**
 * 将文字引擎逐行度量聚合为正文逻辑边界。
 *
 * @param lines - 文字引擎逐行度量。
 * @returns 至少存在一行时的合并边界，否则返回 null。
 */
export const unionLineBounds = (lines: readonly LineMetric[]): Bounds | null =>
    lines.reduce<Bounds | null>(
        (bounds, line) =>
            unionBounds(bounds, {
                left: line.leftEdgeX,
                top: line.top,
                right: line.rightEdgeX,
                bottom: line.bottom,
            }),
        null,
    );

/**
 * 将四向边界平移到目标坐标系。
 *
 * @param bounds - 平移前的可见边界。
 * @param offset - 目标坐标系中的 X/Y 平移量。
 * @returns 平移后的边界；输入为空时返回 null。
 */
export const translateBounds = (bounds: Bounds | null, offset: { x: number; y: number }): Bounds | null =>
    bounds
        ? {
              left: bounds.left + offset.x,
              top: bounds.top + offset.y,
              right: bounds.right + offset.x,
              bottom: bounds.bottom + offset.y,
          }
        : null;
